import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import React from 'react';

import { StatusPill } from '@/components/common/DataTable/StatusPill';
import { IntakeLink } from '@/components/entity-links/entities/IntakeLink';
import { MatterLink } from '@/components/entity-links/entities/MatterLink';
import { SearchResult } from '@/services/api/searchService';

const MAX_VISIBLE = 5;

interface DuplicateWarningPanelProps {
  duplicates: SearchResult[];
  onDismiss: () => void;
}

export const DuplicateWarningPanel: React.FC<DuplicateWarningPanelProps> = ({
  duplicates,
  onDismiss,
}) => {
  const visible = duplicates.slice(0, MAX_VISIBLE);
  const overflowCount = duplicates.length - MAX_VISIBLE;

  return (
    <AnimatePresence>
      {duplicates.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="overflow-hidden"
        >
          <div className="bg-amber-50 rounded-xl border border-amber-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-bold text-amber-800">
                  Possible duplicates
                </span>
                <span className="text-xs font-semibold text-amber-600 bg-amber-100 rounded-full px-2 py-0.5">
                  {duplicates.length}
                </span>
              </div>
              <button
                type="button"
                onClick={onDismiss}
                className="p-1 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition-colors"
                aria-label="Dismiss duplicates warning"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Match rows */}
            <div className="border-t border-amber-200">
              {visible.map((match, index) => (
                <MatchRow key={match.entity_id} match={match} index={index} />
              ))}
            </div>

            {/* Overflow footer */}
            {overflowCount > 0 && (
              <div className="px-4 py-2 border-t border-amber-200 text-center">
                <span className="text-xs font-medium text-amber-600">
                  +{overflowCount} more
                </span>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const MatchRow: React.FC<{ match: SearchResult; index: number }> = ({
  match,
  index,
}) => {
  const isIntake = match.entity_type === 'intake';
  const url = isIntake
    ? `/intakes/${match.entity_id}`
    : `/matters/${match.entity_id}`;
  const status = match.metadata?.status as string | undefined;
  const firmName = match.metadata?.firm_name as string | undefined;
  const LinkComponent = isIntake ? IntakeLink : MatterLink;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2 }}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between px-4 py-2.5 hover:bg-amber-100/60 transition-colors group"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900 truncate">
            <LinkComponent entityId={match.entity_id} openInNewTab>
              {match.label}
            </LinkComponent>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs font-medium text-zinc-500 capitalize">
              {match.entity_type}
            </span>
            {status && (
              <>
                <span className="text-zinc-300">·</span>
                <StatusPill status={status} size="sm" />
              </>
            )}
            {firmName && (
              <>
                <span className="text-zinc-300">·</span>
                <span className="text-xs font-medium text-zinc-500 truncate max-w-[140px]">
                  {firmName}
                </span>
              </>
            )}
          </div>
        </div>
        <ExternalLink className="w-3.5 h-3.5 text-zinc-400 group-hover:text-amber-600 shrink-0 ml-2 transition-colors" />
      </a>
    </motion.div>
  );
};
